import type {
    WeekAvailability} from "@@/shared/date-time/availability/week-availability";
import {
    weekAvailabilityZodSchema,
} from "@@/shared/date-time/availability/week-availability";
import { now } from "@@/shared/date-time/date-time";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeRange} from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { type Discount } from "@@/shared/discounts/discount";
import {
    geoLocationZodSchema,
    type GeoLocation,
} from "@@/shared/locations/geo-location";
import {
    metaContentZodSchema,
    type MetaContent,
} from "@@/shared/meta-content/meta-content";
import {
    pickedOptionalZodSchema,
    type PickedOptional,
} from "@@/shared/optionals/picked-optional";
import { priceZodSchema, zeroPrice, type Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import {
    amountZodSchema,
    type Amount,
} from "@@/shared/units-and-quantities/amount";
import {
    quantityZodSchema,
    type Quantity,
} from "@@/shared/units-and-quantities/quantity";
import { generateId } from "@@/utilities/generate-id";
import { repeat } from "@@/utilities/repeat";
import type { Except } from "type-fest";
import { z } from "zod";
import type { acquireType} from "../../acquire/acquirable";
import { type AcquireType } from "../../acquire/acquirable";
import {
    bookableSettingsZodSchema,
    type BookableSettings,
} from "../../acquire/bookable-settings";
import { bookableTypes, type BookableType } from "../../acquire/bookable-type";
import { assertNever } from "../../assert-never";
import { productIdZodSchema, type ProductId } from "../../products/product-id";
import type { Sku } from "../../products/sku";
import { skuIdZodSchema, type SkuId } from "../../products/sku-id";
import {
    providerIdZodSchema,
    type ProviderId,
} from "../../providers/provider-id";
import { orderItemIdZodSchema_V1, type OrderItemId_V1 } from "./order-item-id";

type _OrderItemBase_V1 = {
    readonly _id: OrderItemId_V1;
    readonly _type: "ORDER_ITEM";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly meta: MetaContent;
    readonly providerId: ProviderId;
    readonly location?: GeoLocation;
    readonly quantity: Quantity;
    readonly messageToProvider: string | null;
    readonly price: Price;
    readonly pricePerItem: Price;
    readonly originalPrice?: Price;
    readonly originalPricePerItem?: Price;
    readonly skuId: SkuId;
    readonly discounts: Discount[];
    readonly articleNumber?: string;
    readonly productId: ProductId;
    readonly pickedOptionals: PickedOptional[];
    readonly availability?: WeekAvailability;
    /**
     * Is/was stock balanced tracked for this order item \
     * If it is, when order is placed, stock will be decreased \
     * and when order is canceled, stock will be increased
     * @type {boolean}
     */
    readonly trackStockBalance?: boolean;
};
/** @type {*} */
const _orderItemBaseZodSchema_V1: z.ZodSchema<_OrderItemBase_V1> = z.object({
    _id: orderItemIdZodSchema_V1,
    _type: z.literal("ORDER_ITEM"),
    _createdAt: isoAndUnixZodSchema,
    meta: metaContentZodSchema,
    providerId: providerIdZodSchema,
    location: geoLocationZodSchema.optional(),
    quantity: quantityZodSchema,
    messageToProvider: z.string().nullable(),
    discounts: z.array(z.any()),
    price: priceZodSchema,
    pricePerItem: priceZodSchema,
    originalPrice: priceZodSchema.optional(),
    originalPricePerItem: priceZodSchema.optional(),
    skuId: skuIdZodSchema,
    articleNumber: z.string().optional(),
    productId: productIdZodSchema,
    pickedOptionals: z.array(pickedOptionalZodSchema),
    availability: weekAvailabilityZodSchema.optional(),
    stockTracked: z.boolean().optional(),
});

type OrderItemBuyable_V1 = _OrderItemBase_V1 & {
    readonly acquireType: typeof acquireType.BUYABLE;
    readonly time?: never;
    readonly reservations?: never;
    readonly settings?: never;
    /**
     * All new voucher order items has this property
     * @type {Amount}
     */
    readonly amount?: Amount;
};
const orderItemBuyableZodSchema_V1: z.ZodSchema<OrderItemBuyable_V1> =
    z.intersection(
        _orderItemBaseZodSchema_V1,
        z.object({
            acquireType: z.literal("BUYABLE"),
            reservation: z.never().or(z.undefined()),
            time: z.never().or(z.undefined()),
            reservations: z.never().or(z.undefined()),
            settings: z.never().or(z.undefined()),
            amount: amountZodSchema.optional(),
        }),
    );

type OrderItemBookable_V1 = _OrderItemBase_V1 & {
    readonly acquireType: typeof acquireType.BOOKABLE;
    readonly time: TimeRange;
    /**
     * Order items before summer 2022 don't have this property
     * @type {BookableType}
     */
    readonly bookingType?: BookableType;

    readonly settings: BookableSettings;

    readonly hasAccess?: boolean;
};
const orderItemBookableZodSchema_V1: z.ZodSchema<OrderItemBookable_V1> =
    z.intersection(
        _orderItemBaseZodSchema_V1,
        z.object({
            acquireType: z.literal("BOOKABLE"),
            time: timeRangeZodSchema,
            bookingType: z.enum(bookableTypes).optional(),
            settings: bookableSettingsZodSchema,
            hasAccess: z.boolean(),
        }),
    );

type OrderItemVoucher_V1 = _OrderItemBase_V1 & {
    readonly acquireType: typeof acquireType.VOUCHER;
    readonly time?: never;
    readonly reservations?: never;
    readonly settings?: never;
    /**
     * All new voucher order items has this property
     * @type {Amount}
     */
    readonly amount?: Amount;
};

type OrderItemGiftCard_V1 = _OrderItemBase_V1 & {
    readonly acquireType: typeof acquireType.GIFTCARD;
    readonly time?: never;
    readonly reservations?: never;
    readonly settings?: never;
    /**
     * All new voucher order items has this property
     * @type {Amount}
     */
    readonly amount?: Amount;
};

const orderItemGiftCardZodSchema_V1: z.ZodSchema<OrderItemGiftCard_V1> =
    z.intersection(
        _orderItemBaseZodSchema_V1,
        z.object({
            acquireType: z.literal("GIFTCARD"),
            time: z.never().or(z.undefined()),
            reservations: z.never().or(z.undefined()),
            settings: z.never().or(z.undefined()),
            amount: amountZodSchema.optional(),
        }),
    );

const orderItemVoucherZodSchema_V1: z.ZodSchema<OrderItemVoucher_V1> =
    z.intersection(
        _orderItemBaseZodSchema_V1,
        z.object({
            acquireType: z.literal("VOUCHER"),
            time: z.never().or(z.undefined()),
            reservations: z.never().or(z.undefined()),
            settings: z.never().or(z.undefined()),
            amount: amountZodSchema.optional(),
        }),
    );

type OrderItem_V1 =
    | OrderItemBuyable_V1
    | OrderItemBookable_V1
    | OrderItemVoucher_V1
    | OrderItemGiftCard_V1;

const orderItemZodSchema_V1 = z.union([
    orderItemBuyableZodSchema_V1,
    orderItemBookableZodSchema_V1,
    orderItemVoucherZodSchema_V1,
    orderItemGiftCardZodSchema_V1,
]);

type OrderItemBuyableFactoryParams_V1 = Except<
    OrderItemBuyable_V1,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId_V1 };
const orderItemBuyableFactory_V1 = (
    params: OrderItemBuyableFactoryParams_V1,
): OrderItemBuyable_V1 => {
    return {
        _id: params._id ?? generateId({ prefix: "order_item__" }),
        _createdAt: nowIsoAndUnix(),
        _type: "ORDER_ITEM",
        acquireType: "BUYABLE",
        ...params,
    };
};

type OrderItemBookableFactoryParams_V1 = Except<
    OrderItemBookable_V1,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId_V1 };
const orderItemBookableFactory_V1 = (
    params: OrderItemBookableFactoryParams_V1,
): OrderItemBookable_V1 => {
    return {
        _id: params._id ?? generateId({ prefix: "order_item__" }),
        _createdAt: now(),
        _type: "ORDER_ITEM",
        acquireType: "BOOKABLE",
        ...params,
    };
};

type OrderItemVoucherFactoryParams_V1 = Except<
    OrderItemVoucher_V1,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId_V1 };
const orderItemVoucherFactory_V1 = (
    params: OrderItemVoucherFactoryParams_V1,
): OrderItemVoucher_V1 => {
    return {
        _id: params._id ?? generateId({ prefix: "order_item__" }),
        _createdAt: now(),
        _type: "ORDER_ITEM",
        acquireType: "VOUCHER",
        ...params,
    };
};

type OrderItemGiftCardFactoryParams_V1 = Except<
    OrderItemGiftCard_V1,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId_V1 };
const orderItemGiftCardFactory_V1 = (
    params: OrderItemGiftCardFactoryParams_V1,
): OrderItemGiftCard_V1 => {
    return {
        _id: params._id ?? generateId({ prefix: "order_item__" }),
        _createdAt: now(),
        _type: "ORDER_ITEM",
        acquireType: "GIFTCARD",
        ...params,
    };
};

function orderItemFactory_V1(
    type: "BUYABLE",
    input: OrderItemBuyableFactoryParams_V1,
): OrderItemBuyable_V1;
function orderItemFactory_V1(
    type: "BOOKABLE",
    input: OrderItemBookableFactoryParams_V1,
): OrderItemBookable_V1;
function orderItemFactory_V1(
    type: "VOUCHER",
    input: OrderItemVoucherFactoryParams_V1,
): OrderItemVoucher_V1;
function orderItemFactory_V1(
    type: "GIFTCARD",
    input: OrderItemGiftCardFactoryParams_V1,
): OrderItemVoucher_V1;
function orderItemFactory_V1(
    type: AcquireType,
    input:
        | OrderItemBuyableFactoryParams_V1
        | OrderItemBookableFactoryParams_V1
        | OrderItemVoucherFactoryParams_V1
        | OrderItemGiftCardFactoryParams_V1,
): OrderItem_V1 {
    switch (type) {
        case "BUYABLE":
            return orderItemBuyableFactory_V1(
                input as OrderItemBuyableFactoryParams_V1,
            );
        case "BOOKABLE":
            return orderItemBookableFactory_V1(
                input as OrderItemBookableFactoryParams_V1,
            );
        case "VOUCHER":
            return orderItemVoucherFactory_V1(
                input as OrderItemVoucherFactoryParams_V1,
            );
        case "GIFTCARD":
            return orderItemGiftCardFactory_V1(
                input as OrderItemGiftCardFactoryParams_V1,
            );

        case "ACCOMMODATION":
            throw new Error("ACCOMMODATION NOT IMPLEMENTED FOR V1");
        default:
            assertNever(type);
    }
}

/**
 * Sum all prices available in cart item to get the
 * full and complete price of the cart item
 * @param {{
 *     sku: Sku,
 *     pickedOptions: PickedOptional[],
 * }} params
 * @returns {Price}
 */
const calculateOrderItemPrice_V1 = (params: {
    sku: Sku;
    pickedOptions: PickedOptional[];
}): Price => {
    const pricesToSum = [
        params.sku.acquire.price,
        ...(params.pickedOptions?.flatMap(pickedOption =>
            repeat(pickedOption, pickedOption.quantity.value).map(_ =>
                pickedOption.optional._type === "OPTIONAL"
                    ? pickedOption.optional.price
                    : zeroPrice,
            ),
        ) || []),
    ];
    const itemPrice = sumPrices(pricesToSum);

    return itemPrice;
};

// const orderItemFromSku_V1 = (params: {
//     sku: Sku;
//     product: Product;
//     discounts: Discount[];
//     quantity: Quantity;
//     pickedOptions: PickedOptional[];
//     messageToProvider?: string;
//     options?: {
//         metaOverrides?: Partial<MetaContent>;
//     };
//     time?: TimeRange;
//     settings?: BookableSettings;
//     resourceReservations?: ResourceReservationRequest[];
// }): OrderItem_V1 => {
//     // Parse all prices from sku and picked options
//     // their combined sum will be the price of the cart item
//     const {
//         sku,
//         product,
//         quantity,
//         pickedOptions,
//         messageToProvider,
//         options,
//         discounts,
//         time,
//         resourceReservations,
//         settings,
//     } = params;

//     // Sum all prices available in cart item to get the
//     // full and complete price of the cart item
//     const pricePerItem = calculateOrderItemPrice_V1({
//         sku: params.sku,
//         pickedOptions: params.pickedOptions,
//     });

//     const numberOfBookedTime = time
//         ? inMinutes({ seconds: time.duration })
//         : (0 as DurationInMinutes);

//     const resourcePrice = sumPrices(
//         (resourceReservations ?? []).flatMap(booking =>
//             booking.optionValues.flatMap(optionValue =>
//                 bookableOptionPriceCalculator({
//                     bookedMinutes: numberOfBookedTime,
//                     quantity: isQuantityPickerBookableOptionValue(optionValue)
//                         ? optionValue.quantity.value
//                         : 0,
//                     option: optionValue,
//                 })
//             )
//         )
//     );

//     let price =
//         product.acquirableAs !== "BOOKABLE"
//             ? sumPrices(repeat(pricePerItem, quantity.value))
//             : sumPrices([
//                   pricePerItem, //Original Price (Base price)
//                   resourcePrice,
//               ]);

//     const percentages = params.pickedOptions.flatMap(f =>
//         f.optional._type === "OPTIONAL_PERCENTAGE" ? f.optional : []
//     ) as OptionalPercentage[];

//     price = sumPrices([
//         price,
//         ...percentages.map(f =>
//             priceFactory({
//                 amountIncludingVat: price.amountIncludingVat * f.percentage,
//                 vat: price.vat,
//             })
//         ),
//     ]);

//     const item = createOrderItem_V1({
//         sku,
//         pickedOptions,
//         discounts,
//         product,
//         messageToProvider,
//         options,
//         pricePerItem,
//         price,
//         quantity,
//         time,
//         settings,
//     });
//     // eslint-disable-next-line no-console

//     if (item.discounts.length > 0) {
//         let discountedPriceBookableOptions = zeroPrice;
//         if (item.acquireType === "BOOKABLE") {
//             const values = (resourceReservations ?? []).flatMap(booking =>
//                 booking.optionValues.flatMap(optionValue => optionValue)
//             );
//             discountedPriceBookableOptions = sumPrices(
//                 values.map(
//                     f =>
//                         calculateDiscountedPriceOnOptionValue(discounts)(
//                             item,
//                             f
//                         ).totalPrice
//                 )
//             );
//         }
//         const itemWithoutOptionValue = {
//             ...item,
//             pricePerItem: pricePerItem,
//             price: sumPrices(repeat(pricePerItem, quantity.value)),
//         };

//         const bestDiscountOrderItem = calculateDiscountedPrice(discounts)(
//             itemWithoutOptionValue
//         );
//         const discountedPrice = sumPrices([
//             bestDiscountOrderItem,
//             discountedPriceBookableOptions,
//         ]);

//         const discountedPriceWithOptionals = sumPrices([
//             discountedPrice,
//             ...percentages.map(f =>
//                 priceFactory({
//                     amountIncludingVat:
//                         discountedPrice.amountIncludingVat * f.percentage,
//                     vat: discountedPrice.vat,
//                 })
//             ),
//         ]);

//         const items2 = {
//             ...item,
//             pricePerItem: discountedPriceWithOptionals,
//             price: discountedPriceWithOptionals,
//         };

//         return items2;
//     }
//     return item;
// };

// const createOrderItem_V1 = (params: {
//     sku: Sku;
//     pickedOptions: PickedOptional[];
//     discounts: Discount[];
//     product: Product;
//     messageToProvider: string | undefined;
//     options: { metaOverrides?: Partial<MetaContent> | undefined } | undefined;
//     pricePerItem: Price;
//     price: Price;
//     quantity: Quantity;
//     time: TimeRange | undefined;
//     settings: BookableSettings | undefined;
// }) => {
//     const {
//         sku,
//         pickedOptions,
//         discounts,
//         product,
//         messageToProvider,
//         options,
//         price,
//         pricePerItem,
//         quantity,
//         time,
//         settings,
//     } = params;

//     /**
//      * create a unique but reproducable id
//      * for every unique cart item.
//      * uniqueness based on sku id and picked option ids
//      */

//     const cartItemId = generateCartItemId(sku, pickedOptions);

//     switch (sku.acquire._type) {
//         case "BUYABLE": {
//             const orderItem: OrderItemBuyable_V1 = {
//                 _id: cartItemId,
//                 _type: "ORDER_ITEM",
//                 _createdAt: now(),
//                 acquireType: sku.acquire._type,
//                 skuId: sku._id,
//                 productId: sku.productId,
//                 discounts,
//                 providerId: product.providerId,
//                 articleNumber: sku.articleNumber
//                     ? sku.articleNumber
//                     : undefined,
//                 messageToProvider: messageToProvider || null,
//                 amount: sku.acquire.amount,
//                 meta: metaContentFactory({
//                     ...sku.meta,
//                     title: product.meta.title,
//                     kicker: product.meta.kicker,
//                     subtitle: sku.meta.title || product.meta.title,
//                     description: getSkuDescription(sku),
//                     ...(options?.metaOverrides || {}),
//                 }),
//                 pricePerItem: pricePerItem,
//                 price: price,
//                 originalPricePerItem: pricePerItem,
//                 originalPrice: price,
//                 quantity,
//                 pickedOptionals: pickedOptions || [],
//                 availability: product.availability
//                     ? product.availability
//                     : undefined,
//                 trackStockBalance: sku.trackStockBalance,
//             };
//             return orderItem;
//         }
//         case "BOOKABLE": {
//             if (!time || !settings) throw new Error("NO RESERVATION");

//             const orderItemBook: OrderItemBookable_V1 = {
//                 _id: cartItemId,
//                 _type: "ORDER_ITEM",
//                 _createdAt: now(),
//                 acquireType: sku.acquire._type,
//                 skuId: sku._id,
//                 productId: sku.productId,
//                 bookingType: sku.acquire.type ? sku.acquire.type : undefined,
//                 time: time,
//                 discounts,
//                 providerId: product.providerId,
//                 articleNumber: sku.articleNumber
//                     ? sku.articleNumber
//                     : undefined,
//                 messageToProvider: messageToProvider || null,
//                 meta: metaContentFactory({
//                     ...sku.meta,
//                     title: product.meta.title,
//                     kicker: product.meta.kicker,
//                     subtitle: sku.meta.title || product.meta.title,
//                     description: getSkuDescription(sku),
//                     ...(options?.metaOverrides || {}),
//                 }),
//                 pricePerItem: price, //price === priceperitem
//                 price: price,
//                 originalPricePerItem: pricePerItem,
//                 originalPrice: price,
//                 quantity,
//                 settings: settings,
//                 pickedOptionals: pickedOptions || [],
//                 availability: product.availability
//                     ? product.availability
//                     : undefined,
//                 trackStockBalance: sku.trackStockBalance,
//                 hasAccess: !!sku.acquire.hasAccess,
//             };

//             return orderItemBook;
//         }
//         case "VOUCHER": {
//             const orderItem: OrderItemVoucher_V1 = {
//                 _id: cartItemId,
//                 _type: "ORDER_ITEM",
//                 _createdAt: now(),
//                 acquireType: sku.acquire._type,
//                 skuId: sku._id,
//                 productId: sku.productId,
//                 discounts,
//                 providerId: product.providerId,
//                 articleNumber: sku.articleNumber
//                     ? sku.articleNumber
//                     : undefined,
//                 messageToProvider: messageToProvider || null,
//                 meta: metaContentFactory({
//                     ...sku.meta,
//                     title: product.meta.title,
//                     kicker: product.meta.kicker,
//                     subtitle: sku.meta.subtitle || product.meta.subtitle,
//                     description: product.meta.description,
//                     images: product.meta.images, //maybe sku but we dont have it.
//                     ...(options?.metaOverrides || {}),
//                 }),
//                 amount: sku.acquire.amount,
//                 pricePerItem: pricePerItem,
//                 price: price,
//                 originalPricePerItem: pricePerItem,
//                 originalPrice: price,
//                 quantity,
//                 pickedOptionals: pickedOptions || [],
//                 availability: product.availability
//                     ? product.availability
//                     : undefined,
//                 trackStockBalance: sku.trackStockBalance,
//             };
//             return orderItem;
//         }
//         case "GIFTCARD": {
//             const orderItem: OrderItemGiftCard_V1 = {
//                 _id: cartItemId,
//                 _type: "ORDER_ITEM",
//                 _createdAt: now(),
//                 acquireType: sku.acquire._type,
//                 skuId: sku._id,
//                 productId: sku.productId,
//                 discounts,
//                 providerId: product.providerId,
//                 articleNumber: sku.articleNumber
//                     ? sku.articleNumber
//                     : undefined,
//                 messageToProvider: messageToProvider || null,
//                 meta: metaContentFactory({
//                     ...sku.meta,
//                     title: product.meta.title,
//                     kicker: product.meta.kicker,
//                     subtitle: sku.meta.subtitle || product.meta.subtitle,
//                     description: product.meta.description,
//                     images: product.meta.images, //maybe sku but we dont have it.
//                     ...(options?.metaOverrides || {}),
//                 }),
//                 amount: sku.acquire.amount,
//                 pricePerItem: pricePerItem,
//                 price: price,
//                 originalPricePerItem: pricePerItem,
//                 originalPrice: price,
//                 quantity,
//                 pickedOptionals: pickedOptions || [],
//                 availability: product.availability
//                     ? product.availability
//                     : undefined,
//                 trackStockBalance: sku.trackStockBalance,
//             };
//             return orderItem;
//         }
//         default:
//             throw new Error(
//                 // eslint-disable-next-line @typescript-eslint/no-explicit-any
//                 "Unhandled acquire type; " + (sku.acquire as any)._type
//             );
//     }
// };

export {
    calculateOrderItemPrice_V1,
    orderItemBookableFactory_V1,
    orderItemBookableZodSchema_V1,
    orderItemBuyableFactory_V1,
    orderItemBuyableZodSchema_V1,
    orderItemFactory_V1,
    orderItemVoucherFactory_V1,
    orderItemVoucherZodSchema_V1,
    orderItemZodSchema_V1,
};
export type {
    OrderItem_V1,
    OrderItemBookable_V1,
    OrderItemBuyable_V1,
    OrderItemGiftCard_V1,
    OrderItemVoucher_V1,
};
